import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ConsumptionRecord {
  consumption_id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  quantity: number;
  location_id: string;
  location_name: string;
  department_id: string;
  department_name: string;
  patient_id?: string;
  service_id?: string;
  notes?: string;
  consumed_at: string;
  created_by: string;
  created_at: string;
}

export interface ConsumptionSummary {
  department_id: string;
  department_name: string;
  total_items: number;
  total_quantity: number;
  total_value: number;
  period_start: string;
  period_end: string;
}

export interface DepartmentUsageReport {
  departments: ConsumptionSummary[];
  total_consumption: number;
  total_value: number;
  top_items: Array<{
    item_id: string;
    item_name: string;
    quantity: number;
    value: number;
  }>;
}

export interface DailyConsumptionStats {
  date: string;
  record_count: number;
  total_quantity: number;
  total_value: number;
}

export function useConsumption() {
  const queryClient = useQueryClient();

  const getConsumptionRecords = (params?: {
    department_id?: string;
    location_id?: string;
    item_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }) =>
    useQuery<ConsumptionRecord[]>({
      queryKey: ['consumption-records', params],
      queryFn: () => api.get('/consumption', { params }).then((res) => res.data),
    });

  const getDepartmentSummary = (params?: {
    start_date?: string;
    end_date?: string;
  }) =>
    useQuery<DepartmentUsageReport>({
      queryKey: ['consumption-summary', params],
      queryFn: () =>
        api
          .get('/consumption/summary/by-department', { params })
          .then((res) => res.data),
    });

  const getDailyStats = (days: number = 7) =>
    useQuery<DailyConsumptionStats[]>({
      queryKey: ['consumption-daily-stats', days],
      queryFn: () =>
        api.get('/consumption/stats/daily', { params: { days } }).then((res) => res.data),
    });

  const createConsumption = () =>
    useMutation({
      mutationFn: (data: {
        item_id: string;
        quantity: number;
        location_id: string;
        department_id: string;
        patient_id?: string;
        service_id?: string;
        notes?: string;
        consumed_at?: string;
      }) => api.post('/consumption', data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['consumption-records'] });
        queryClient.invalidateQueries({ queryKey: ['consumption-summary'] });
        queryClient.invalidateQueries({ queryKey: ['consumption-daily-stats'] });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    });

  const createBulkConsumption = () =>
    useMutation({
      mutationFn: (
        records: Array<{
          item_id: string;
          quantity: number;
          location_id: string;
          department_id: string;
          patient_id?: string;
          service_id?: string;
          notes?: string;
        }>
      ) => api.post('/consumption/bulk', records),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['consumption-records'] });
        queryClient.invalidateQueries({ queryKey: ['consumption-summary'] });
        queryClient.invalidateQueries({ queryKey: ['consumption-daily-stats'] });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    });

  const deleteConsumption = () =>
    useMutation({
      mutationFn: (consumptionId: string) => api.post(`/consumption/${consumptionId}/delete`),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['consumption-records'] });
        queryClient.invalidateQueries({ queryKey: ['consumption-summary'] });
        queryClient.invalidateQueries({ queryKey: ['consumption-daily-stats'] });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    });

  return {
    getConsumptionRecords,
    getDepartmentSummary,
    getDailyStats,
    createConsumption,
    createBulkConsumption,
    deleteConsumption,
  };
}
